import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, IconLookup } from 'component-library-and-tools';
import { isEmpty } from 'lodash-es';

import Button from 'shared/components/Button';
import { getExtractedTerms } from 'services/safelists/getters';
import { closeModal as closeModalActionCreator } from 'services/modal/actions';
import { IndexableObjectType } from 'shared/types/common';
import { SafelistTerm } from 'shared/types/safelist';
import { colors } from 'lib/prisma-styles';

import { getSelectedTerms } from '../utilities';
import { SAFELIST_TERM_MANAGER_MODAL } from '../';

import { ReviewAreaControlBar } from './ReviewAreaControlBar';
import { RedactableTermItems } from './RedactableTermItems';
import { TermDictionary, TermReviewAreaProps } from './types';
import {
  addTermManuallyToExtractedTerms,
  updateSafelistTerm,
  removeExtractedTerm,
} from 'services/safelists/actions';
import { IndicatorType } from 'shared/types/transformation';

const StyledTermReviewAreaContainer = styled.div`
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 13.167rem 0 2.5rem;
  box-sizing: border-box;
`;

const StyledReviewAreaHeader = styled.div`
  margin: 1.5rem 0 3.34rem;
`;

const StyledReviewAreaTitle = styled.h1`
  font-size: 2.34rem;
  font-weight: 700;
`;

const StyledReviewAreaContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const StyledReviewAreaSubtitle = styled.h4`
  margin: 0 0 1.5834rem;
  font-size: 1.167rem;
  font-weight: 400;
`;

const StyledReviewAreaControlBar = styled(ReviewAreaControlBar)`
  margin-bottom: 1.34rem;
  padding: 0 0 0 1.34rem;
`;

const StyledRedactableTermItems = styled(RedactableTermItems)`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.167rem;
`;

const StyledNoTermsMessageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledNoTermsMessage = styled.span`
  font-size: 1rem;
  color: #8d9199;
`;

const StyledReviewAreaFooter = styled.div`
  margin: 1.3rem 0;
`;

const StyledButton = styled(Button)`
  && {
    font-weight: 700;

    &:not(:last-child) {
      margin-right: 3.67rem;
    }
  }
`;

const StyledSubmitButton = styled(StyledButton)`
  height: 3.67rem;
`;

const StyledCancelButton = styled(StyledButton)`
  && {
    min-width: unset;
    min-height: unset;
    padding: 0;
    background: none;
    color: ${colors.content.default};
  }
`;

const SEARCH_FILTER_KEY = 'search';
const TYPE_FILTER_KEY = 'type';

const filterTermByName = (terms: SafelistTerm[], searchValue: string) => (
  terms.filter((term) => term.entity.match(new RegExp(searchValue, 'i')))
);

const filterTermByType = (terms: SafelistTerm[], selectedTypes: string[]) => {
  if (!selectedTypes.length) {
    return terms;
  }

  return terms.filter((term) => selectedTypes.includes(term.type));
};

const FILTER_MAPPER: IndexableObjectType = {
  [SEARCH_FILTER_KEY]: filterTermByName,
  [TYPE_FILTER_KEY]: filterTermByType,
};

const filterTerms = (terms: SafelistTerm[], filters: Object) => {
  if (isEmpty(filters)) {
    return terms;
  }

  let filteredTerms = terms;

  Object.entries(filters).forEach(([filterKey, filterValues]) => {
    const filterFn = FILTER_MAPPER[filterKey];

    filteredTerms = filterFn(filteredTerms, filterValues);
  })

  return filteredTerms;
};

const adaptTermListToDictionary = (terms: (SafelistTerm & {isBeingEdited?: boolean})[]) => {
  const termDictionary: IndexableObjectType = {};

  terms.forEach((term) => {
    termDictionary[term.entity] = {
      term,
      isSelected: false,
      isBeingEdited: term.isBeingEdited ? true : false,
    };
  });

  return termDictionary;
};

/**
 * Checks if the list of terms has only manual terms.
 */
const hasOnlyManuallyAddedTerms = (extractedTerms: SafelistTerm[]) => extractedTerms.every(({ entity }) => !entity);

const TermReviewArea = ({
  className,
  onSubmit,
}: TermReviewAreaProps) => {
  const dispatch = useDispatch();
  const [termDictionary, setTermDictionary] = useState<TermDictionary>({});
  const extractedTerms = useSelector(getExtractedTerms);

  const [filters, setFilters] = useState({});

  const selectedTerms = getSelectedTerms(termDictionary);
  const numOfExtractedTerms = extractedTerms.length;
  const hasOnlyManuallyTerms = hasOnlyManuallyAddedTerms(extractedTerms);

  useEffect(() => {
    const filteredTerms = filterTerms(extractedTerms, filters);

    setTermDictionary(adaptTermListToDictionary(filteredTerms));
  }, [JSON.stringify(extractedTerms), JSON.stringify(filters)]);

  const closeModal = useCallback(() => {
    dispatch(closeModalActionCreator(SAFELIST_TERM_MANAGER_MODAL));
  }, []);

  const onUpdateTerm = (originalTerm: SafelistTerm, updatedTerm: SafelistTerm) => {
    dispatch(updateSafelistTerm(originalTerm,updatedTerm));
  }

  const onFilterChange = (filterKey: string, filterValues: any) => {
    setFilters({
      ...filters,
      [filterKey]: filterValues,
    });
  };

  const onSelectAll = (allSelected: boolean) => {
    const updatedTermDictionary: IndexableObjectType = {};

    Object.entries(termDictionary).forEach(([key, value]) => {
      updatedTermDictionary[key] = {
        ...value,
        isSelected: allSelected,
      };
    });

    setTermDictionary(updatedTermDictionary);
  };

  const onSelectTerm = (selectedTerm: SafelistTerm, selectedStatus: boolean) => {
    const updatedTermDictionary = {
      ...termDictionary,
      [selectedTerm.entity]: {
        term: selectedTerm,
        isSelected: selectedStatus,
      },
    };

    setTermDictionary(updatedTermDictionary);
  };

  const onEditTerm = (selectedTerm: SafelistTerm, isEditing: boolean, shouldDeleteTerm?: boolean) => {  
    if (shouldDeleteTerm) {
      dispatch(removeExtractedTerm(selectedTerm));
    } else {
      const updatedTermDictionary = {
        ...termDictionary,
        [selectedTerm.entity]: {
          ...termDictionary[selectedTerm.entity],
            isBeingEdited: isEditing,
        }
      };
  
      setTermDictionary(updatedTermDictionary);
    }
  };

  const onRemoveTerm = (term: SafelistTerm) => {
    dispatch(removeExtractedTerm(term));
  };

  const handleSubmitClick = () => {
    onSubmit(selectedTerms);
    closeModal();
  };

  const addNewExtractedTermManually = () => {
    dispatch(addTermManuallyToExtractedTerms({
      type: IndicatorType.URL,
      entity: '',
    }));
  };

  return (
    <StyledTermReviewAreaContainer className={className}>
      <StyledReviewAreaHeader>
        <StyledReviewAreaTitle>
          Add Safelist Items
        </StyledReviewAreaTitle>
      </StyledReviewAreaHeader>
      {!numOfExtractedTerms
        ? <StyledNoTermsMessageContainer>
            <NoExtractedTerms />
          </StyledNoTermsMessageContainer>
        : <StyledReviewAreaContent>
            {!hasOnlyManuallyTerms && (
              <StyledReviewAreaSubtitle>
                {`We found ${numOfExtractedTerms} items. Select which one you want to add and specify how to index URLs.`}
              </StyledReviewAreaSubtitle>
            )}
            <StyledReviewAreaControlBar
              allSelected={!!selectedTerms.length && selectedTerms.length === numOfExtractedTerms}
              onCheckDisabled={hasOnlyManuallyTerms}
              onSelectAll={onSelectAll}
              onAddNewTerm={addNewExtractedTermManually}
              onSearch={(searchValue) => onFilterChange(SEARCH_FILTER_KEY, searchValue)}
              onSelectType={(selectedTypes) => onFilterChange(TYPE_FILTER_KEY, selectedTypes)}
            />
            <StyledRedactableTermItems
              termDictionary={termDictionary}
              onSelect={onSelectTerm}
              onUpdateTerm={onUpdateTerm}
              onEditTerm={onEditTerm}
              onRemoveTerm={onRemoveTerm}
            />
          </StyledReviewAreaContent>
      }
      <StyledReviewAreaFooter>
        <StyledSubmitButton isDisabled={!selectedTerms.length} label='Add Safelist Items' onClick={handleSubmitClick} />
        <StyledCancelButton label='Cancel' onClick={closeModal} />
      </StyledReviewAreaFooter>
    </StyledTermReviewAreaContainer>
  );
};


// Helper Components


// Empty State Components
const AddNewTermIcon = styled(Icon)`
  padding: 0.4rem;
  margin: 0.25rem 0;

  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const NoExtractedTerms = () => {
  const dispatch = useDispatch();
  const addNewExtractedTermManually = useCallback(() => {
    dispatch(addTermManuallyToExtractedTerms({
      type: IndicatorType.URL,
      entity: '',
    }));
  }, [])
  return (
    <StyledNoTermsMessageContainer>
      <StyledNoTermsMessage>Type or paste a string on the left and analyze it.</StyledNoTermsMessage>
      <StyledNoTermsMessage>You can also begin by manually adding items</StyledNoTermsMessage> <AddNewTermIcon onClick={addNewExtractedTermManually} icon={IconLookup.PLUS_CIRCLE} fontSize={'2rem'}/>
      <StyledNoTermsMessage>You can manually add items regardless of whether you use text analysis tool.</StyledNoTermsMessage>
    </StyledNoTermsMessageContainer>
  )
}

export { TermReviewArea };
